import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CheckCircle, Circle, X, Buildings, Sparkle, ChartBar, FileText, Crown, UserCircle, ArrowRight, Lightning, Trash,
} from '@phosphor-icons/react';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import useEventMode from '@/hooks/useEventMode';
import { toolsHubPath } from '@/lib/toolRoutes';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = 'growthriq_setup_checklist_dismissed_v1';
const TOOL_TRIED_KEY = 'growthriq_used_free_tool_v1';

const STEP_META = (t, language) => ({
  account: {
    icon: UserCircle,
    title: t('checklist.s1.title'),
    body: t('checklist.s1.body'),
    cta: null,
  },
  company: {
    icon: Buildings,
    title: t('checklist.s2.title'),
    body: t('checklist.s2.body'),
    cta: { to: '/companies', label: t('checklist.s2.cta') },
  },
  tool: {
    icon: Sparkle,
    title: t('checklist.s3.title'),
    body: t('checklist.s3.body'),
    cta: { to: toolsHubPath(language), label: t('checklist.s3.cta') },
  },
  assessment: {
    icon: ChartBar,
    title: t('checklist.s4.title'),
    body: t('checklist.s4.body'),
    cta: { to: '/companies', label: t('checklist.s4.cta') },
  },
  report: {
    icon: FileText,
    title: t('checklist.s5.title'),
    body: t('checklist.s5.body'),
    cta: { to: '/dashboard', label: t('checklist.s5.cta') },
  },
  upgrade: {
    icon: Crown,
    title: t('checklist.s6.title'),
    body: t('checklist.s6.body'),
    cta: { to: '/pricing', label: t('checklist.s6.cta') },
  },
});

export default function GettingStartedChecklist() {
  const { t, language } = useLanguage();
  const { getAuthHeaders } = useAuth();
  const { enabled: eventMode } = useEventMode();
  const [data, setData] = useState(null);
  const [demoBusy, setDemoBusy] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(DISMISS_KEY) === '1';
  });

  const applyToolFlag = (payload) => {
    if (typeof window !== 'undefined' && localStorage.getItem(TOOL_TRIED_KEY) === '1') {
      const steps = payload.steps.map((s) => (s.id === 'tool' ? { ...s, done: true } : s));
      const completed = steps.filter((s) => s.done).length;
      return { ...payload, steps, completed, all_done: completed === payload.total };
    }
    return payload;
  };

  const loadStatus = async () => {
    try {
      const { data: payload } = await axios.get(`${API_URL}/dashboard/onboarding-status`, {
        headers: getAuthHeaders(),
      });
      setData(applyToolFlag(payload));
    } catch (_e) {
      // silent — checklist is non-critical
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: payload } = await axios.get(`${API_URL}/dashboard/onboarding-status`, {
          headers: getAuthHeaders(),
        });
        if (!cancelled) setData(applyToolFlag(payload));
      } catch (_e) {
        /* silent */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (dismissed || !data) return null;
  // Auto-hide when all done — show a final celebration banner once, then can be dismissed
  const meta = STEP_META(t, language);

  // Event Mode: drop the "upgrade" step entirely — paywalls are off, so
  // nudging the user to buy a plan is off-message during the demo event.
  const visibleSteps = eventMode ? data.steps.filter((s) => s.id !== 'upgrade') : data.steps;
  const visibleCompleted = visibleSteps.filter((s) => s.done).length;
  const visibleTotal = visibleSteps.length;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  const handleLoadDemo = async () => {
    setDemoBusy(true);
    try {
      await axios.post(`${API_URL}/dashboard/demo-data?lang=${language}`, null, {
        headers: getAuthHeaders(),
      });
      toast.success(t('checklist.demo.success'));
      await loadStatus();
      // Trigger a hard refresh so the rest of the dashboard (stats, recent
      // assessments) re-fetches and reflects the demo data immediately.
      setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t('checklist.demo.fail'));
    } finally {
      setDemoBusy(false);
    }
  };

  const handleRemoveDemo = async () => {
    setDemoBusy(true);
    try {
      await axios.delete(`${API_URL}/dashboard/demo-data`, { headers: getAuthHeaders() });
      toast.success(t('checklist.demo.removed'));
      await loadStatus();
      setTimeout(() => window.location.reload(), 600);
    } catch (_e) {
      toast.error(t('checklist.demo.fail'));
    } finally {
      setDemoBusy(false);
    }
  };

  const pct = Math.round((visibleCompleted / visibleTotal) * 100);
  const showDemoCta = !data.has_demo_data && visibleCompleted <= 2;

  return (
    <div
      className="bg-white border border-[#E4E4E7] mb-8 overflow-hidden"
      data-testid="setup-checklist"
    >
      {/* Header */}
      <div className="px-6 py-5 border-b border-[#E4E4E7] flex items-start sm:items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Sparkle size={18} weight="duotone" className="text-[#3CD9B8]" />
            <h2 className="font-bold text-[#09090B] text-base sm:text-lg">
              {(visibleCompleted === visibleTotal) ? t('checklist.title.done') : t('checklist.title')}
            </h2>
          </div>
          <p className="text-sm text-[#52525B]">
            {(visibleCompleted === visibleTotal) ? t('checklist.subtitle.done') : t('checklist.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right">
            <div className="text-2xl font-bold text-[#0F1B36] leading-none" data-testid="checklist-progress-count">
              {visibleCompleted}/{visibleTotal}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-[#52525B] mt-0.5">
              {t('checklist.progress')}
            </div>
          </div>
          <button
            onClick={handleDismiss}
            aria-label="dismiss"
            data-testid="checklist-dismiss"
            className="p-1.5 rounded-md text-[#52525B] hover:bg-[#F4F4F5] hover:text-[#09090B] transition"
          >
            <X size={16} weight="bold" />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-[#F4F4F5]">
        <div
          className="h-full bg-[#3CD9B8] transition-all duration-500"
          style={{ width: `${pct}%` }}
          data-testid="checklist-progress-bar"
        />
      </div>

      {/* Demo data CTA (early-stage users only) */}
      {showDemoCta && (
        <div className="px-6 py-4 bg-[#0F1B36] text-white flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4" data-testid="checklist-demo-cta">
          <div className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[#3CD9B8]/15 text-[#3CD9B8]">
            <Lightning size={20} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">{t('checklist.demo.title')}</p>
            <p className="text-xs text-white/60 mt-0.5">{t('checklist.demo.body')}</p>
          </div>
          <button
            onClick={handleLoadDemo}
            disabled={demoBusy}
            data-testid="checklist-load-demo"
            className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#3CD9B8] text-[#0F1B36] text-xs font-bold hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Sparkle size={14} weight="fill" />
            {demoBusy ? t('checklist.demo.loading') : t('checklist.demo.cta')}
          </button>
        </div>
      )}

      {data.has_demo_data && (
        <div className="px-6 py-3 bg-[#FEF3C7] text-[#92400E] flex items-center justify-between gap-3 text-xs" data-testid="checklist-demo-banner">
          <div className="flex items-center gap-2 min-w-0">
            <Lightning size={14} weight="fill" className="shrink-0" />
            <span className="truncate font-medium">{t('checklist.demo.active')}</span>
          </div>
          <button
            onClick={handleRemoveDemo}
            disabled={demoBusy}
            data-testid="checklist-remove-demo"
            className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-md hover:bg-[#FBBF24]/30 font-semibold transition"
          >
            <Trash size={12} weight="bold" /> {t('checklist.demo.remove')}
          </button>
        </div>
      )}

      {/* Steps */}
      <ul className="divide-y divide-[#F4F4F5]">
        {visibleSteps.map((s, idx) => {
          const m = meta[s.id];
          if (!m) return null;
          const Icon = m.icon;
          return (
            <li
              key={s.id}
              className={`px-6 py-4 flex items-center gap-4 ${s.done ? 'bg-[#3CD9B8]/[0.04]' : ''}`}
              data-testid={`checklist-step-${s.id}`}
            >
              <div className="shrink-0">
                {s.done ? (
                  <CheckCircle size={26} weight="fill" className="text-[#3CD9B8]" />
                ) : (
                  <Circle size={26} weight="regular" className="text-[#E4E4E7]" />
                )}
              </div>
              <div className="shrink-0 hidden sm:flex items-center justify-center w-9 h-9 rounded-lg bg-[#F4F4F5]">
                <Icon size={18} weight="duotone" className="text-[#0F1B36]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`font-semibold text-sm ${s.done ? 'text-[#52525B] line-through decoration-1' : 'text-[#09090B]'}`}>
                  <span className="text-[#52525B] mr-1">{idx + 1}.</span>{m.title}
                </p>
                <p className="text-xs text-[#52525B] mt-0.5">{m.body}</p>
              </div>
              {!s.done && m.cta && (
                <Link
                  to={m.cta.to}
                  data-testid={`checklist-step-${s.id}-cta`}
                  className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#0F1B36] text-white text-xs font-semibold hover:opacity-90 transition whitespace-nowrap"
                >
                  {m.cta.label} <ArrowRight size={12} weight="bold" />
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
